import { useEffect, useRef, useState } from "react";
import "../../src/ui/tokens.css";
import "../../src/ui/floating.css";
import "../../src/ui/menu.css";
import { displayCurrencies, type DisplayCurrency } from "../../src/lib/currency-rates";
import type { CurrencyRates } from "../../src/lib/currency-rates";
import { currencyRatesLoader } from "../../src/lib/currency-rates-loader";
import {
  chooseDisplayCurrency,
  readDisplayCurrency,
  showPricesIn,
  subscribeDisplayCurrency,
} from "../../src/lib/display-currency";
import { loadCurrencyRates } from "../server/currency-rates";
import { Menu } from "./ui-kit/menu";

const readRates = currencyRatesLoader(() => loadCurrencyRates());

/**
 * The header's currency picker (Customize › Header › Show currency selector): prices on the page
 * follow the visitor's choice, kept in this browser. The page is drawn in the creator's own
 * currencies first, so the choice applies once it runs; until then the picker names `currency`,
 * the one the site's prices are in (shell.server.ts).
 */
export function CurrencyPicker({
  creatorId,
  currency,
  id,
}: {
  creatorId: string;
  currency: DisplayCurrency;
  id: string;
}) {
  const root = useRef<HTMLDivElement>(null);
  const [chosen, setChosen] = useState<DisplayCurrency | null>(null);
  const [rates, setRates] = useState<CurrencyRates | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Start once the header mounts, before a visitor opens the picker.
    void readRates().then((value) => {
      if (!cancelled) setRates(value);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const read = () => setChosen(readDisplayCurrency(creatorId));
    read();
    return subscribeDisplayCurrency(read);
  }, [creatorId]);

  useEffect(() => {
    return showPricesIn(chosen, rates);
  }, [chosen, rates]);

  useEffect(() => {
    const element = root.current;
    if (!element) return;
    const select = (event: Event) => {
      const value = (event as CustomEvent<{ value: string }>).detail.value;
      const picked = displayCurrencies.find((code) => code === value);
      // Kept even when it is the site's own: a price in another currency still converts.
      if (picked) {
        chooseDisplayCurrency(creatorId, picked);
        // Retry after an initial failure, still shared between the page's pickers.
        if (!rates) void readRates().then(setRates);
      }
    };
    element.addEventListener("ui-menu-select", select);
    return () => element.removeEventListener("ui-menu-select", select);
  }, [creatorId, rates]);

  const current = (rates ? (chosen ?? currency) : currency).toUpperCase();
  return (
    <div ref={root} className="header-currency">
      <Menu
        id={id}
        label={current}
        ariaLabel={`Currency: ${current}`}
        align="end"
        triggerClass="header-currency-trigger"
        items={displayCurrencies.map((code) => ({ label: code.toUpperCase(), value: code }))}
      />
    </div>
  );
}
